import React, { createContext, useContext, useState, useEffect } from "react";
import { callAPI, Method } from "../commonFunctions/CommonFunctions";
import { useAuth } from "./AuthContext";

export interface DashboardData {
    totalItems: number;
    totalStock: number;
    lowStockItems: number;
    pendingCheques: number;
    todayAdjustments: number;

    stockMovement: Record<string, number>;

    chequeSummary: Record<string, number>;

    monthlySummary: Record<string, number>;

    lowStockList: {
        ItemId: string;
        ItemName: string;
        Quantity: number;
        ReorderLevel: number;
        Status: string;
    }[];
}

interface DashboardContextType {
    data: DashboardData | null;
    loading: boolean;
    refresh: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token } = useAuth();

    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchDashboard = async () => {
        if (!token) return; // prevent call without token

        try {
            setLoading(true);
            const response = await callAPI("dashboard/", Method.GET);
            setData(response.data);
        } catch (err) {
            console.error("Failed to fetch dashboard data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, [token]); //run ONLY when token is ready

    return (
        <DashboardContext.Provider
            value={{
                data,
                loading,
                refresh: fetchDashboard
            }}
        >
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboard must be used within DashboardProvider");
    }
    return context;
};